import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { MessageRef } from './MessageRef';
import { Messages, newMessage, Token } from "../../model/Message";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import messagesQuery from "../../firestore/Messages.query";
import { auth } from "../../firebase";
import { Groups } from "../../model/Groups";
import { Company } from "../../model/Companies";
import { User } from "../../model/Users";
import { Spiner } from "../../components/spinner/Spiner";
import { ReplyButton } from "../../model/ReplyButton";
import { OnlineUrls } from "../../model/OnlineUrls";
import Button, { BtnProps } from "../../components/button/Button";
import { MessageEditModal } from "./MessageEditModal";
import { GridCol, GridRow } from "../../components/table/TableList";
import { MessagePreviewModal } from "./MessagePreviewModal";
import { GroupSelectModal } from "./GroupSelectModal";
import { UserSelectModal } from "./UserSelectModal";
import { MessagingIntController } from "../../controller/MessagingInitController";
import { DeliveryResultTally } from "../../components/replyResultTally/DeliveryResultTally";
import { GroupSelect } from "../../controller/GroupSelectController";
import { UserSelect } from "../../controller/UserSelectController";
import { MessagingCommonFunctions } from "../../controller/MessagingCommonFunctionsController";
import { ConstMessage } from "../../config/const";
import { createEvenReplyButtons } from "../../utility/CreateEventReplyButtonUtility";
import { ModalAndAlmightyDialog } from "../../components/modal/ModalAndAlmightyDialog";
import { EventCodePDF } from "../../components/pdf/EventCodePDF";
import QRCode from "qrcode";
import { pdf } from "@react-pdf/renderer";
import { convertDateToStringCompactFormat } from "../../utility/ConvertUtily";
import { QuestionnaireForm } from "./QuestionnaireModal";
import userQuery from "../../firestore/User.query";
import companiesQuery from "../../firestore/Companies.query";
import { checkOption, isOptionActive } from "../../controller/OptionController";
import { requestGetTargetUser } from "../../api/Users.api";

export const MessageInfo = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDisabled = useRef(false);
  const href = useRef(window.location.href);

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  // URLパラメータからmessageId取得
  const { messageId } = useParams<{ messageId: string }>();

  const messageInit = new MessagingIntController();
  const messageCommonFunctions = new MessagingCommonFunctions();
  const groupSelect = new GroupSelect();
  const userSelect = new UserSelect();

  const delayLoadFlg = useRef<boolean>(false);
  const userListLoadFlg = useRef<boolean>(false);

  // MessageList・MessageInfo・Schedule共通
  const [initFlg, setInitFlg] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [isOpenPreview, setIsOpenPreview] = useState<boolean>(false);
  const [company, setCompany] = useState<Company>();
  const [groupList, setGroupList] = useState<Groups[]>([]);
  const [filteredGroupList, setFilteredGroupList] = useState<Groups[]>([]);
  const [userList, setUserList] = useState<User[]>([]);
  const [filteredUserList, setFilteredUserList] = useState<User[]>(userList);
  const [tokenList, setTokenList] = useState<Token[]>([]);
  const [targetUserList, setTargetUserList] = useState<User[]>([]);
  const [replyButtonList, setReplyButtonList] = useState<ReplyButton[]>([]);
  const [replyButtonSelected, setReplyButtonSelected] = useState<ReplyButton | undefined>();
  const [defaultReplyButtonList, setDefaultReplyButtonList] = useState<ReplyButton[]>([]);
  const [eventReplyButtonList, setEventReplyButtonList] = useState<ReplyButton[]>([]);
  const [onlineUrlList, setOnlineUrlList] = useState<OnlineUrls[]>([]);
  const [onlineUrlSelected, setOnlineUrlSelected] = useState<string>();
  const [topicState, setTopicState] = useState<string[]>([]);
  const [tokenState, setTokenState] = useState<string[]>([]);
  const [token, setToken] = useState<Token[]>([]);
  const [isGroupSelectOpen, setIsGroupSelectOpen] = useState(false);
  const [isUserSelectOpen, setIsUserSelectOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireForm | undefined>(undefined);
  const [base64Image, setBase64Image] = useState<string>('');
  const [previewData, setPreviewData] = useState<Messages>();
  const [groupFormRowList, setGroupFormRowList] = useState<GridRow[]>([]);
  const [groupCheckedIdList, setGroupCheckedIdList] = useState<string[]>(topicState);
  const [userFormRowList, setUserFormRowList] = useState<GridRow[]>([]);
  const [userCheckedIdList, setUserCheckedIdList] = useState<string[]>(tokenState);
  const [selectedRowList, setSelectedRowList] = useState<GridRow[]>([]);

  const [message, setMessage]  = useState<Messages>(newMessage);

  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [dialogChildren, setDialogChildren] = useState<React.ReactNode | undefined>();
  const [dialogActions, setDialogActions] = useState<BtnProps[]>([]);
  const [dialogTitle, setDialogTitle] = useState<string>('');

  const deliveryRef = useRef<HTMLDivElement | null>(null);
  const routerLocation = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    if (params.get("scrollTo") === "deliveryResult" && deliveryRef.current) {
      // 配信先一覧にスクロール
      requestAnimationFrame(() => {
        deliveryRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, [routerLocation.search, message]);

  const [optionFlg, setOptionFlg] = useState<{ point: boolean; attendance: boolean }>({
    point: false,
    attendance: false
  });
  
  // オプション設定出し分け
  async function fetchOptions() {
    const options = await companiesQuery.getOptionContractList(currentUser?.tenantId!);
    // ポイント管理のステータス、期間チェック
    const pointOption = options.find((option) => option.optionId === 'point');
    const point = pointOption ? isOptionActive(pointOption) : false;
    // 出欠管理（二次元コード）のステータス、期間チェック
    const attendanceOption = options.find((option) => option.optionId === 'attendance');
    const attendance = attendanceOption ? isOptionActive(attendanceOption) : false;
    return { point, attendance };
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await fetchOptions();
      setOptionFlg(result);
    }
  
    loadOptions();
  }, []);

  // 初期データ取得 MessageList・MessageInfo・Scheduleほぼ共通
  useMemo(() => {
    const f = async () => {
      try {
        setIsLoad(true);
        await messageInit.main(
          currentUser?.tenantId!,
          messageId ?? '',
          setCompany,
          setGroupList,
          setFilteredGroupList,
          setReplyButtonList,
          setDefaultReplyButtonList,
          undefined,
          setTokenList,
          setEventReplyButtonList,
          setOnlineUrlList
        );
        // 初期表示のデータを全て取得した後にスピナー表示を終了する
        setIsLoad(false);
      } catch (err :any) {
        dispatch(alertOpen(AlertValue("お知らせを取得できません", "danger")));
        setIsLoad(false);
      }
    }
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.tenantId, dispatch]);

  // お知らせデータ作成
  useEffect(() => {
    const f = async () => {
      try{
        if(messageId){
          // お知らせ取得
          const message = await messagesQuery.getMessage(messageId);
          message.authorName = message.authorName
            ?? userList.filter((user) => user.id === message.authorUid).at(0)?.lastName
            ?? '';

          // 返信ボタン取得
          const replyButtonSelected = await messagesQuery.getReplyButton(messageId);
          setReplyButtonSelected(replyButtonSelected);

          // アンケートデータ取得
          const questionnaireData = await messagesQuery.getQuestionnaire(messageId);
          setQuestionnaireData(questionnaireData); 

          // ミーティングURL設定
          const onlineUrlsUrl = onlineUrlList.filter((url) => url.url === message.link).at(0);
          if(onlineUrlsUrl !== undefined) {
            // ミーティングURL一覧から選択されている場合
            setOnlineUrlSelected(onlineUrlsUrl.url);
          }
          if(onlineUrlsUrl === undefined && message.link !== 'none'){
            message.url = message.link;
            setOnlineUrlSelected('');
          }

          const topic = await messagesQuery.getTopic(messageId);
          message.topic = topic.map(t => t.id);
          if(message.select ==='token'){
            const getTargetUserData = await requestGetTargetUser(company?.id ?? '', tokenList.map((t) => t.userId), true, false);
            if (getTargetUserData.success) setTargetUserList(getTargetUserData.users);

            if(message.topic.length > 0){
              message.topic = [];
              setTopicState(topicState.splice(0,Infinity));
            }
          }
          setMessage(message);
          setSelectedEventType(message.eventType ?? "");
          setBase64Image(message.image);
        } else {
          throw new Error();
        }
      } catch (err :any) {
        let errorMessage = err.error;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(
            alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
          );
        } else {
          dispatch(alertOpen(AlertValue("お知らせを取得できません", "danger")));
        }
        setIsLoad(false);
      }
    }
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dispatch, messageId, onlineUrlList, isOpen, company?.eventType]);

  // お知らせ削除
  const onClickDelete = async () =>{
    try {
      await messagesQuery.deleteTopic(message?.id);
      await messagesQuery.deleteMessage(message?.id);
      dispatch(alertOpen(AlertValue("削除しました。", "success")));
      navigate("/message", { state: message.id })
    } catch (err) {
      console.error(err)
      dispatch(alertOpen(AlertValue("削除に失敗しました", "danger")));
    }
  }

  // グループ選択後の表のカラム MessageList・MessageInfo・Schedule共通
  const selectedColumns = groupSelect.selectedColumns;

  useEffect(() => {
    // グループ選択後の表のデータ MessageList・MessageInfo・Schedule共通
    const selectedRowList = groupSelect.selectedRowList(groupList, topicState);

    // グループ選択時に個別で選択された場合に個別を表に追加するデータ
    tokenState.forEach((userId) => {
      const userData = userList?.filter((user) => user.id === userId)[0];
      selectedRowList.push({
        rowData: {
          name: (<div className="whitespace-normal">{userData?.lastName}</div>),
          explanation: (<div className="whitespace-normal">個別配信</div>),
        }
      });
    });
    setSelectedRowList(selectedRowList);
    setGroupCheckedIdList(topicState);
    setUserCheckedIdList(tokenState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[groupList, topicState, tokenState, userList]);

  // 配信先グループ選択モーダルカラム MessageList・MessageInfo・Schedule共通
  const groupFormColumns: GridCol[] = groupSelect.groupFormColumns;

  // 配信先利用者選択モーダルカラム MessageList・MessageInfo・Schedule共通
  const userFormColumns = userSelect.userFormColumns;

  // グループ選択 MessageList・MessageInfo・Schedule共通
  const onSelectTopic = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    groupSelect.groupCheckedId(e, id, groupCheckedIdList, setGroupCheckedIdList);
  };
  // 個別配信選択 MessageList・MessageInfo・Schedule共通
  const onSelectToken = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    userSelect.userCheckedId(e, id, userCheckedIdList, setUserCheckedIdList);
  };

  // 配信先グループ選択モーダルデータ MessageList・MessageInfo・Schedule共通
  useEffect(() => {
    const groupFormRowList = groupSelect.groupFormRowList(filteredGroupList, onSelectTopic, groupCheckedIdList);
    setGroupFormRowList(groupFormRowList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[filteredGroupList, topicState, groupCheckedIdList]);

  // 配信先利用者選択モーダルデータ MessageList・MessageInfo・Schedule共通
  useEffect(() => {
    const userListRow = userSelect.userListRow(filteredUserList, onSelectToken, userCheckedIdList);
    setUserFormRowList(userListRow);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[filteredUserList, tokenState, userCheckedIdList]);

  // 配信先グループ検索 MessageList・MessageInfo・Schedule共通
  const onSearch = (e: any) => groupSelect.search(e, groupList, setFilteredGroupList);

  // 配信先利用者検索 MessageList・MessageInfo・Schedule共通
  const onUserSearch = (e: any) => userSelect.search(e, userList, setFilteredUserList);

  // 配信先グループ選択モーダル戻り値 MessageList・MessageInfo・Schedule共通
  const onClickGroupSubmit = () => {
    setUserCheckedIdList([]);
    setTokenState([]);
    setToken([]);
    setTopicState(groupCheckedIdList);
    selectToggle();
  };

  // 配信先利用者選択モーダル戻り値 MessageList・MessageInfo・Schedule共通
  const onClickUserSubmit = () => {
    setGroupCheckedIdList([]);
    setTopicState([]);
    setToken([]);
    setTokenState(userCheckedIdList);
    selectUserToggle();
  };

  // 配信先グループ選択モーダル開閉トリガー MessageList・MessageInfo・Schedule共通
  const selectToggle = () => {
    setIsGroupSelectOpen(!isGroupSelectOpen);
    const e = {target:{value: ''}}
    onSearch(e);
  };
  // 配信先利用者選択モーダル開閉トリガー MessageList・MessageInfo・Schedule共通
  const selectUserToggle = () => {
    setIsUserSelectOpen(!isUserSelectOpen);
    const e = {target:{value: ''}}
    onUserSearch(e);
  };

  // お知らせプレビュー MessageList・MessageInfo・Schedule共通
  const onClickPreview = async (data: Messages) => {
    await messageDatetimeSet(data, 'preview');
  };
  // お知らせ追加 MessageList・MessageInfo・Schedule共通
  const onClickSubmit = async () => {
      await messageDatetimeSet(previewData!, 'send');
  };
  // お知らせ下書き MessageList・MessageInfo・Schedule共通
  const onDraft = async (data: Messages) => {
      await messageDatetimeSet(data, 'draft');
  };

  // お知らせの日時関連をまとめてセット MessageList・MessageInfo・Scheduleほぼ共通
  const messageDatetimeSet = async (data: Messages, type: 'draft' | 'send' | 'preview')
    : Promise<void> => {
    if(!isDisabled.current){
      let replyButtonFlg = false;
      try{
        isDisabled.current = true;
        if(data.eventFlg){
          const result = createEvenReplyButtons().some(replyButton => replyButton.name == data.replyButton);
          if(!result){
            replyButtonFlg = true;
            data.replyButton = "";
          }
        }
        const {sendDateList, sendData, sendToken} = await messageCommonFunctions.submit(
          data, type, onlineUrlSelected, currentUser,
          selectedEventType, base64Image, company,
          token, setToken, tokenState, topicState
        );

        // Firestore登録
        sendData.draftFlg = type === 'draft' || type === 'preview';
        // 下書き
        if(type === 'draft'){
          // 配信フラグ
          sendData.deliveredFlg = false;
          // tokenのdeliverdFlg更新
          sendToken.forEach((to) => {
            to.deliveredFlg = false;
            to.sendDate = sendData.sendDate;
          });
          await messagesQuery.updateMessage(sendData, sendToken, replyButtonFlg? undefined : replyButtonSelected, questionnaireData);
          dispatch(alertOpen(AlertValue("下書き保存しました。", "success")));
          toggle();
          // お知らせToken取得
          const tokenList = await messagesQuery.getMunicipalitiesToken(currentUser?.tenantId!);
          setTokenList(tokenList);
          isDisabled.current = false;
        } else if(type === 'send'){
          // 配信
          await messagesQuery.deleteTopic(sendData.id);
          await messagesQuery.deleteMessage(sendData.id);
          sendData.id = '';
          const result = await Promise.all(sendDateList.map( async (sendDateAndEventDateList) => {
            sendData.sendDate = sendDateAndEventDateList.sendDate;
            sendData.eventAt = sendDateAndEventDateList.eventAt ?? new Date();
            sendData.eventEndAt = sendDateAndEventDateList.eventEndAt ?? new Date();
            // 配信フラグ
            sendData.deliveredFlg =sendData.sendDate < new Date();
            // tokenのdeliverdFlg更新
            sendToken.forEach((to) => {
              to.deliveredFlg = sendData.sendDate < new Date();
              to.sendDate = sendData.sendDate;
            });
            return await messagesQuery.addMessage(sendData, sendToken, replyButtonSelected, questionnaireData);
          }));
          if(result.length === 0){
            throw new Error();
          }
          dispatch(alertOpen(AlertValue(`${sendData.eventFlg?'予定':'お知らせ'}を作成しました`, "success")));
          toggle();
          togglePreview();
          isDisabled.current = false;
          delayLoadFlg.current = true;
          navigate(`/message/${result.at(0)}/info`);
          // お知らせToken取得
          const tokenList = await messagesQuery.getMunicipalitiesToken(currentUser?.tenantId!);
          setTokenList(tokenList);
        } else {
          // プレビュー
          setPreviewData(sendData);
          togglePreview();
          isDisabled.current = false;
        };
      } catch (err: any) {
        if (err === "ERR_NETWORK") {
          dispatch(
            alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
          );
        } else {
          console.error(err)
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        isDisabled.current = false;
      }
    }
  };
  // 返信ボタンの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onReplyButtonSelect = (id: string) => {
    setReplyButtonSelected(replyButtonList?.filter((item) => item.id === id)[0]);
  }
  // ミーティングURLの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onOnlineUrlSelect = (url: string) => {
    setOnlineUrlSelected(url);
  }
  // イベントタイプの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onEventTypeSelect = (eventType: string) => {
    setSelectedEventType(eventType);
  };

  // アンケートの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onQuestionnaire = (data: QuestionnaireForm) => {
    setQuestionnaireData(data);
  };

  // 画像をbase64に変換して文字列としてステートに保存 MessageList・MessageInfo・Schedule共通
  const onSendImage = (file: File) => {
    try{
      if(file.type !== 'image/jpeg' && file.type !== 'image/png'){
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_UNSUPPORT_IMG_FILE_TYPE, "danger")));
        return;
      };
      messageCommonFunctions.imageFileToBase64Image(file, setBase64Image);
    } catch (error: any){
      console.error(error);
      dispatch(alertOpen(AlertValue("アップロードに失敗しました", "danger")));
    }
  };

  // お知らせ入力モーダルの開閉トリガー
  const toggle = async () => {
    setIsOpen(!isOpen);

    if (userListLoadFlg.current === false) {
      userListLoadFlg.current = true;
      // 利用者を取得
      const getTargetUserData = await requestGetTargetUser(company?.id ?? '', [], true, false);
      if (getTargetUserData.success) {
        setUserList(getTargetUserData.users);
        const filteredUserList = getTargetUserData.users.filter((user) => user.lastName !== undefined && user.lastName !== '' && !user.deleteFlg && !user.accountSuspensionFlg);
        setFilteredUserList(filteredUserList);
        setInitFlg(true);
      }
    }
  };

  // プレビューモーダルの開閉トリガー MessageList・MessageInfo・Schedule共通
  const togglePreview = () => {
    setIsOpenPreview(!isOpenPreview);
  };

  const pdfDownloadConfirmation = () => {
    const actions: BtnProps[] = [
      {
        color: "indigo",
        outline: true,
        children: "キャンセル",
        className:"w-[132px]",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
        }
      },
      {
        color: "indigo",
        children: "ダウンロード",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
          EventCodePdfDownload();
        }
      },
    ];
    const dialogChildren = (
      <>
        二次元コードPDFをダウンロードしますか？
      </>
    );
    setIsOpenDialog(true);
    setDialogChildren(dialogChildren);
    setDialogActions(actions);
  };

  const toggleDialog = () => {
    setIsOpenDialog(isOpenDialog);
  };

  const initDialog = () => {
    setDialogTitle("");
    setDialogChildren("");
    setDialogActions([]);
  };

  const EventCodePdfDownload = async () => {

    try {
      // QRコード画像データ生成
      const qrCodeData = await QRCode.toDataURL(`${message.id}`, { errorCorrectionLevel: 'M' });

      // PDFダウンロード
      const blob = await pdf(<EventCodePDF message={message} qrCodeData={qrCodeData} />).toBlob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `二次元コード_${convertDateToStringCompactFormat(message.eventAt)}_${message.title}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error(err);
      dispatch(
        alertOpen(AlertValue(ConstMessage.ERR_PDF_DOWNLOAD, "danger"))
      );
    }     
  };

  /** 一覧へボタンの遷移先URLの設定 */
  const getDestinatione = async () => {
    let destinationeUrl = "/message";

    if (href.current.includes('transitionSource=eventParticipation')) {
      // イベント参加履歴一覧画面へ遷移
      destinationeUrl = "/eventParticipation";
    } else if (href.current.includes('transitionSource=userList_')) {
      const userListUrl = 'userList_';
      const targetUserId = href.current.substring(href.current.indexOf(userListUrl) + userListUrl.length);
      try {
        const targetUser = await userQuery.getUser(currentUser?.tenantId!, targetUserId);
        if (targetUser.deleteAt === null || targetUser.deleteAt === undefined) {
          // 利用者詳細画面へ遷移
          destinationeUrl = `/userList/${targetUserId}/info`;
        }
      } catch (e: any) {
        return destinationeUrl;
      }
    }

    return destinationeUrl;
  };

  const isFromEventParticipation = () => {
    if (href.current.includes('transitionSource=eventParticipation') ||
        href.current.includes('transitionSource=userList_')
    ) {
      return true;
    } else {
      return false;
    }
  }

  return (
    <main>
      <div className={"flex flex-col overflow-y-auto h-[90vh] min-w-[370px]"}>
        <nav className="flex items-start px-4 py-3" aria-label="Breadcrumb">
          <div
            className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer"
            onClick={() => (async () => navigate(await getDestinatione(), { state: delayLoadFlg.current ? message.id : null}))()}
          >
            <ChevronLeftIcon
              className="-ml-2 h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
            <span>{isFromEventParticipation() ? "戻る" : "一覧へ"}</span>
          </div>
        </nav>
        {isLoad ? (
          <Spiner />
        ) : (
        <><MessageRef
            value={message}
            replyButton={replyButtonSelected}
            questionnaire={questionnaireData}
            pointFlg={optionFlg.point}
            onlineUrlList={onlineUrlList}
            tokenList={tokenList}
            company={company!}
            groupList={groupList}
            userList={targetUserList} />
          <div className={'flex m-5 pb-6 px-7 justify-end'}>
            {optionFlg.attendance && (!message.draftFlg && message.eventFlg) && (
              <>
                <Button
                  size={"sm"}
                  className={"ml-2"}
                  color={"violet"}
                  onClick={async () => {
                    if (await checkOption("attendance", dispatch)) {
                      pdfDownloadConfirmation();
                      }
                    }
                  }
                >
                  二次元コードPDFダウンロード
                </Button>
              </>
            )}
            <ModalAndAlmightyDialog
              toggle={toggleDialog}
              open={isOpenDialog}
              title={dialogTitle}
              children={dialogChildren}
              actions={dialogActions}
              height={"72"}
              closeOnClickOutside={false} />
            {(message.draftFlg || message.sendDate >= new Date()) && (
              <>
                <Button
                  size={"sm"}
                  className={"ml-2"}
                  onClick={toggle}
                  children={"編集"} />
                <Button
                  color={"red"}
                  size={"sm"}
                  className={"ml-2"}
                  onClick={onClickDelete}
                  children={"削除"} />
                <MessageEditModal
                  open={isOpen}
                  toggle={toggle}
                  message={message}
                  closeOnClickOutside={true}
                  children={undefined}
                  company={company}
                  tokenList={tokenList}
                  replyButtonSelected={replyButtonSelected}
                  replyButtonList={replyButtonList}
                  setReplyButtonList={setReplyButtonList}
                  defaultReplyButtonList={defaultReplyButtonList}
                  eventReplyButtonList={eventReplyButtonList}
                  onlineUrlList={onlineUrlList}
                  onClickPreview={onClickPreview}
                  onDraft={onDraft}
                  onReplyButtonSelect={onReplyButtonSelect}
                  onOnlineUrlSelect={onOnlineUrlSelect}
                  onEventTypeSelect={onEventTypeSelect}
                  selectedEventType={selectedEventType}
                  onSendImage={onSendImage}
                  setBase64Image={setBase64Image}
                  base64Image={base64Image}
                  setTokenState={setTokenState}
                  setTopicState={setTopicState}
                  selectedColumns={selectedColumns}
                  selectedRowList={selectedRowList}
                  setIsGroupSelectOpen={setIsGroupSelectOpen}
                  setIsUserSelectOpen={setIsUserSelectOpen}
                  onlineUrlSelected={onlineUrlSelected}
                  onQuestionnaire={onQuestionnaire}
                  getQuestionnaire={questionnaireData}
                />
                <GroupSelectModal
                  open={isGroupSelectOpen}
                  toggle={selectToggle}
                  closeOnClickOutside={true}
                  children={undefined}
                  onClickGroupSubmit={onClickGroupSubmit}
                  columns={groupFormColumns}
                  rowList={groupFormRowList}
                  onSearch={onSearch} />
                <UserSelectModal
                  open={isUserSelectOpen}
                  toggle={selectUserToggle}
                  closeOnClickOutside={true}
                  children={undefined}
                  initFlg={initFlg}
                  onClickUserSubmit={onClickUserSubmit}
                  columns={userFormColumns}
                  rowList={userFormRowList}
                  onSearch={onUserSearch} />
                <MessagePreviewModal
                  children={undefined}
                  open={isOpenPreview}
                  toggle={togglePreview}
                  closeOnClickOutside={false}
                  data={previewData}
                  token={token}
                  company={company!}
                  groupList={groupList}
                  userList={userList}
                  replyButtonSelected={replyButtonSelected}
                  onClickSubmit={onClickSubmit} />
              </>
            )}
            </div>
            {/* 下書、未配信以外の場合配信先一覧表示 */}
            {message && !message.draftFlg && message.sendDate < new Date() && (
              <div ref={deliveryRef} className="px-3 sm:px-6 lg:px-8">
                <DeliveryResultTally
                  messageId={message.id}
                  replyButton={replyButtonSelected}
                  company={company} />
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};